import { Injectable } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { NgxCaptureService } from 'ngx-capture';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { DocumentService } from '../../document/document.service';
import { CurrentModuleContextService } from 'src/app/services/core/current-module-context.service';
import { CaptureRect, CaptureResult, ScreenshotState } from './screenshot.models';

/**
 * Central controller for the global screenshot feature. The header calls
 * `startCapture()`; the overlay drives selection and calls `capture()`; the
 * toolbar consumes the result and finishes via `copyToClipboard()` or
 * `saveToFolder()` (upload into a picked document folder).
 *
 * State transitions:
 *   idle → selecting → captured → idle
 *
 * The service owns the ngx-capture call so the overlay/toolbar stay purely UI —
 * future changes (crop editor, upload, etc.) plug in here.
 *
 * Rendering only ever covers the smallest element that contains the selection,
 * never the whole page. ngx-capture (html2canvas-pro under the hood) rasterises
 * that subtree and crops against the element's own box, so cropping stays
 * correct however the page is scrolled and whatever `position: fixed` chrome is
 * on screen. The overlay/toolbar are excluded from the shot via the engine's
 * native `data-html2canvas-ignore` attribute — that skips them in the render
 * clone only, so they stay fully visible on screen (no flicker) while the
 * capture runs.
 */
@Injectable({ providedIn: 'root' })
export class ScreenshotService {
  /** Attribute used by overlay/toolbar elements so they're hidden during capture. */
  static readonly IGNORE_ATTR = 'data-screenshot-ignore';

  private stateSubject = new BehaviorSubject<ScreenshotState>('idle');
  private captureSubject = new BehaviorSubject<CaptureResult | null>(null);

  constructor(
    private ngxCapture: NgxCaptureService,
    private toaster: ToasterService,
    private documentService: DocumentService,
    private moduleContext: CurrentModuleContextService
  ) {}

  /** Current state — used by overlay/toolbar to decide what to render. */
  get state$(): Observable<ScreenshotState> {
    return this.stateSubject.asObservable();
  }

  /** Latest captured image, if any. Null while idle or selecting. */
  get capture$(): Observable<CaptureResult | null> {
    return this.captureSubject.asObservable();
  }

  get currentState(): ScreenshotState {
    return this.stateSubject.value;
  }

  get currentCapture(): CaptureResult | null {
    return this.captureSubject.value;
  }

  /**
   * Kept for the header/idle callers that used to pre-load the capture engine.
   * ngx-capture (html2canvas) has no module to lazy-load and needs no font/image
   * pre-caching, so this is now a no-op — the call sites stay valid either way.
   */
  warmUp(): void {
    /* no-op: ngx-capture requires no warm-up */
  }

  /** Entry point — called by the header camera icon. */
  startCapture(): void {
    if (this.stateSubject.value !== 'idle') return;
    this.captureSubject.next(null);
    this.stateSubject.next('selecting');
  }

  /** Called by overlay on ESC / click-outside, and by toolbar close/finish. */
  cancel(): void {
    if (this.stateSubject.value === 'idle') return;
    this.captureSubject.next(null);
    this.stateSubject.next('idle');
  }

  /**
   * Render the smallest element containing the selection with ngx-capture, then
   * crop the selection out of it. Overlay/toolbar elements carry
   * data-screenshot-ignore; we flag them with the engine's native
   * data-html2canvas-ignore for the duration of the render so they're dropped
   * from the shot without ever being hidden on screen.
   */
  async capture(rect: CaptureRect): Promise<void> {
    if (rect.width < 4 || rect.height < 4) {
      this.cancel();
      return;
    }

    const restoreIgnore = this.markIgnoredForCapture();
    try {
      const target = this.pickCaptureElement(rect) as HTMLElement;
      const targetRect = target.getBoundingClientRect();

      // ngx-capture crops in the element's own coordinate space at scale 1,
      // where (0,0) is the element's top-left. Clamp to the element's box so a
      // selection that overruns the target never asks html2canvas for pixels
      // outside the render.
      const crop = {
        x: Math.max(0, Math.round(rect.x - targetRect.left)),
        y: Math.max(0, Math.round(rect.y - targetRect.top)),
        width: Math.min(Math.round(rect.width), Math.floor(targetRect.width)),
        height: Math.min(Math.round(rect.height), Math.floor(targetRect.height)),
      };

      const dataUrl = await firstValueFrom(
        this.ngxCapture.getImage(target, false, crop)
      );

      if (!dataUrl) {
        this.toaster.showSuccessMessage('Screenshot failed. Please try again.');
        this.cancel();
        return;
      }

      // ngx-capture returns a base64 PNG data URL; the rest of the feature works
      // in Blobs (clipboard write, file upload), so convert once here.
      const blob = await (await fetch(dataUrl)).blob();

      this.captureSubject.next({ blob, rect });
      this.stateSubject.next('captured');
    } catch (err) {
      console.error('[Screenshot] capture failed', err);
      this.toaster.showSuccessMessage('Screenshot failed. Please try again.');
      this.cancel();
    } finally {
      restoreIgnore();
    }
  }

  /** Copy captured image to the OS clipboard. */
  async copyToClipboard(): Promise<void> {
    const current = this.captureSubject.value;
    if (!current) return;

    try {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': current.blob }),
      ]);
      this.toaster.showSuccessMessage('Screenshot copied to clipboard');
    } catch (err) {
      console.error('[Screenshot] clipboard write failed', err);
      this.toaster.showSuccessMessage('Unable to copy — clipboard access denied');
    } finally {
      this.cancel();
    }
  }

  /**
   * Upload the captured screenshot into the folder the user picked in the
   * document picker. The destination folder comes from the picker result; the
   * `module_id` / `attachable_id` come from the centralized CurrentModuleContext
   * (the page the user is on — e.g. Bid #16 / bidId), matching how the rest of
   * the app scopes attachments. Posts to `document/upload-file`.
   *
   * @param pickerResult the object DocumentPickerComponent closed with
   *        (`{ module, referenceId, folder, selectedFolder, ... }`).
   */
  saveToFolder(
    pickerResult: any,
    capture: CaptureResult | null = this.captureSubject.value
  ): void {
    const current = capture;
    if (!current) return;

    const ctx = this.moduleContext.getContext();
    // Context wins (the current page); fall back to what the picker resolved.
    const moduleId = ctx.moduleId ?? pickerResult?.module?.id ?? null;
    const referenceId = ctx.referenceId ?? pickerResult?.referenceId ?? null;
    const folderId =
      pickerResult?.selectedFolder?.id ?? pickerResult?.folder?.id ?? null;

    if (moduleId == null) {
      this.toaster.showSuccessMessage('Cannot save screenshot: no module context');
      return;
    }

    // Use the name the user set in the picker (extension locked to .png there);
    // fall back to a timestamped default if the picker didn't return one.
    const fileName =
      typeof pickerResult?.fileName === 'string' && pickerResult.fileName.trim()
        ? pickerResult.fileName.trim()
        : `screenshot-${new Date().getTime()}.png`;
    const file = new File([current.blob], fileName, { type: 'image/png' });

    this.documentService
      .uploadFileWithProgress(file, {
        module_id: moduleId,
        folder_id: folderId,
        attachable_id: referenceId ?? undefined,
        attachment_field: 'image',
      })
      .subscribe({
        next: (event: any) => {
          if (event?.type === HttpEventType.Response) {
            this.toaster.showSuccessMessage('Screenshot saved to documents');
            this.cancel();
          }
        },
        error: (err: any) => {
          console.error('[Screenshot] save to document failed', err);
          this.toaster.showSuccessMessage('Failed to save screenshot to documents');
        },
      });
  }

  // ---------------------------------------------------------------------
  // Capture-root selection
  // ---------------------------------------------------------------------

  /**
   * Flag every data-screenshot-ignore element with the capture engine's native
   * `data-html2canvas-ignore` for the duration of a capture. The engine skips
   * these while cloning the DOM, so the overlay/toolbar never land in the shot —
   * yet nothing is hidden on the live page, so the selection stays visible the
   * whole time (no flicker). Returns a callback that clears the flag again,
   * leaving any element that already carried it untouched.
   */
  private markIgnoredForCapture(): () => void {
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>(`[${ScreenshotService.IGNORE_ATTR}]`)
    );
    const added = nodes.filter((el) => !el.hasAttribute('data-html2canvas-ignore'));
    added.forEach((el) => el.setAttribute('data-html2canvas-ignore', 'true'));
    return () => added.forEach((el) => el.removeAttribute('data-html2canvas-ignore'));
  }

  /**
   * Smallest element whose box fully contains the selection. Probing five
   * points rather than one keeps a selection that spans sibling elements from
   * resolving to an element that only covers part of it.
   */
  private pickCaptureElement(rect: CaptureRect): Element {
    const probes: Array<[number, number]> = [
      [rect.x + 1, rect.y + 1],
      [rect.x + rect.width - 1, rect.y + 1],
      [rect.x + 1, rect.y + rect.height - 1],
      [rect.x + rect.width - 1, rect.y + rect.height - 1],
      [rect.x + rect.width / 2, rect.y + rect.height / 2],
    ];

    const hits = probes
      .map(([x, y]) => this.topmostVisibleAt(x, y))
      .filter((el): el is Element => !!el);

    if (!hits.length) return document.body;

    let node: Element | null = hits.reduce((a, b) => this.commonAncestor(a, b));
    while (node && node !== document.body && !this.containsRect(node, rect)) {
      node = node.parentElement;
    }
    return node ?? document.body;
  }

  /** Topmost element at a point that isn't part of the screenshot UI itself. */
  private topmostVisibleAt(x: number, y: number): Element | null {
    return (
      document
        .elementsFromPoint(x, y)
        .find((el) => !el.closest(`[${ScreenshotService.IGNORE_ATTR}]`)) ?? null
    );
  }

  private commonAncestor(a: Element, b: Element): Element {
    const ancestors = new Set<Element>();
    for (let node: Element | null = a; node; node = node.parentElement) {
      ancestors.add(node);
    }
    for (let node: Element | null = b; node; node = node.parentElement) {
      if (ancestors.has(node)) return node;
    }
    return document.body;
  }

  private containsRect(el: Element, rect: CaptureRect): boolean {
    const box = el.getBoundingClientRect();
    return (
      box.left <= rect.x + 1 &&
      box.top <= rect.y + 1 &&
      box.right >= rect.x + rect.width - 1 &&
      box.bottom >= rect.y + rect.height - 1
    );
  }
}
